import { createStore, emitOnce, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { IAuthUser } from '@app-core/interfaces/auth-user.interface';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
} from 'rxjs';
import { ILoginResponse } from '@app-core/interfaces/login.interface';
import { UserRole } from '@app-shared/enums/user-roles.enum';
import { createKeepMap } from '@app-shared/functions/store.functions';

export interface SessionProps {
  token_key: string | null;
  refresh_token: string | null;
  authUser: IAuthUser | null;
  /** The route that the user was going to before being intercepted by one of the route guards. */
  savedRoute: string | null;
}

const initialProps: SessionProps = {
  token_key: null,
  refresh_token: null,
  authUser: null,
  savedRoute: null,
};

const STORE_NAME = 'sandoz-v2-session';

@Injectable({ providedIn: 'root' })
export class SessionRepository {
  private readonly store;

  public readonly token_key$: Observable<string | null>;
  public readonly refresh_token$: Observable<string | null>;
  public readonly authUser$: Observable<IAuthUser | null>;
  public readonly savedRoute$: Observable<string | null>;

  public readonly isLoggedIn$: Observable<boolean>;
  public readonly isDeveloper$: Observable<boolean>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<SessionProps>(initialProps),
    );

    persistState(this.store, {
      key: STORE_NAME,
      storage: localStorageStrategy,
    });

    this.token_key$ = this.store.pipe(
      select(state => state.token_key),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.refresh_token$ = this.store.pipe(
      select(state => state.refresh_token),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.authUser$ = this.store.pipe(
      select(state => state.authUser),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.savedRoute$ = this.store.pipe(
      select(state => state.savedRoute),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isLoggedIn$ = combineLatest([
      this.token_key$,
      this.refresh_token$,
      this.authUser$,
    ]).pipe(
      map(([token, refresh, user]) => {
        if (token && refresh && user) return true;
        else return false;
      }),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isDeveloper$ = this.authUser$.pipe(
      map(user => {
        if (!user) return false;

        return user.roles.includes(UserRole.DEVELOPER);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getStoreValue(): SessionProps {
    return this.store.getValue();
  }

  getStoreProp<K extends keyof SessionProps>(key: K): SessionProps[K] {
    return this.store.getValue()[key];
  }

  setStoreProp<K extends keyof SessionProps, V extends SessionProps[K]>(
    key: K,
    value: V,
  ): void {
    this.store.update(setProp(key, value));
  }

  resetStore(...keep: (keyof SessionProps)[]): void {
    const keepMap = createKeepMap<SessionProps>(keep, this.getStoreProp);

    this.store.reset();

    // set the values back
    if (keepMap) {
      Array.from(keepMap.entries()).forEach(([k, v]) =>
        this.setStoreProp(k, v),
      );
    }
  }

  /**
   * Updates the token and user store props
   *
   * @param res
   */
  saveLoginInfo(res: ILoginResponse): void {
    emitOnce(() => {
      this.setStoreProp('token_key', res.token_key);
      this.setStoreProp('refresh_token', res.refresh_token);
      this.setStoreProp('authUser', res.user);
    });
  }
}
